/* global TokenExIframeClient */
import { useState, useEffect } from "react";
import { useWizard } from "react-use-wizard";

import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner"; // Import Spinner component

import SummaryTable from "components/SummaryTable";
import Select from "components/Select";
import { useCheckoutContext } from "context/CheckoutContext";

import ShipMethods from "./exigo/ShipMethods";
import { submitOrder, recalculateCart, upgradeToAmbassador, getSavedCreditCard } from "api";
import { API_LOCAL_BASE_URL } from "config";
import { sendErrorToSlack } from "../services/slackService";

const Payment = ({ cart, shouldSkipSubscription }) => {
  const [isValidToken, setValidToken] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isCardValid, setIsCardValid] = useState(true);
  const [orderStatus, setOrderStatus] = useState("invalid");
  const [token, setToken] = useState("");
  const { previousStep, nextStep } = useWizard();
  const { checkout, setCheckout } = useCheckoutContext();
  const [tokenExClient, setTokenExClient] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [monthError, setMonthError] = useState("");
  const [yearError, setYearError] = useState("");
  const [monthValidated, setMonthValidated] = useState(false);
  const [yearValidated, setYearValidated] = useState(false);
  const [nameError, setNameError] = useState("");
  const [nameValidated, setNameValidated] = useState(false);
  const [cvvError, setCvvError] = useState("");
  const [cvvValidated, setCvvValidated] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("existing"); 
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const isUserLoggedIn = Boolean(checkout.customerToken);

  let windowWidth = window.innerWidth;
  let screenSize = windowWidth < 576 ? "98%" : "100%";

  const initializeTokenExClient = async () => {
    try {
      const script = document.createElement("script");
      script.src = `${API_LOCAL_BASE_URL}api/Checkout/TokenEx`;
      script.onload = () => {
        const container = document.getElementById("tokenExIframeDiv");
        if (container) {
          const tokenExClient = new TokenExIframeClient("tokenExIframeDiv", {
            styles: {
              base: `
                display: block;
                width: ${screenSize};
                height: 40px;
                max-height: 40px;
                min-height: 40px;
                padding: .375rem .5rem;
                font-size: 1rem;
                line-height: 1.5;
                color: #212529;
                background-color: #fff;
                background-clip: padding-box;
                border: 1px solid #CED4DA;
                border-radius: .25rem;
                transition: border-color .15s ease-in-out;
                margin: 0;
                font-family: 'Helvetica Neue';
                overflow: hidden;
                box-sizing: border-box;
              `,
              focus: "color: #12345; border: 2px solid #212529; outline:none;",
              error: "color: #212529; border: 1px solid #dc3545; outline:none;",
            },
            origin: window.location,
            tokenExID: "9691041715912503",
          });
          setTokenExClient(tokenExClient);
        }
      };

      document.head.appendChild(script);
    } catch (error) {
      console.error("Error initializing TokenEx:", error);
      sendErrorToSlack(error, {
        component: "Payment-TokenExInitialization",
        userEmail: checkout.customer?.email,
        cartToken: checkout.cartToken,
      });
    }
  };

  useEffect(() => {
    setCheckout({ ...checkout, currentStep: 3, orderStatus: "invalid" });

    if (isUserLoggedIn) {
      const loadSavedCards = async () => {
        try {
          const response = await getSavedCreditCard(checkout.shopUrl, checkout.customerToken);         
          if (response.success && response.savedCard && !response.savedCard.isExpired) {
            const savedCard = {
              id: "primary",
              last4: response.savedCard.last4,
              expiryMonth: response.savedCard.expirationMonth,
              expiryYear: response.savedCard.expirationYear,
              cardType: response.savedCard.cardType,
              nameOnCard: response.savedCard.nameOnCard,
              token: response.savedCard.creditCardToken
            };
            setSavedCards([savedCard]);
            setSelectedCard(savedCard);
          } else {
            setSavedCards([]);
            setSelectedCard(null);
            setPaymentMethod("new");
          }
        } catch (error) {
            console.error("Error loading saved credit card:", error);
            sendErrorToSlack(error, {
              component: "Payment-LoadSavedCards",
              userEmail: checkout.customer?.email,
              cartToken: checkout.cartToken,
            });
            setSavedCards([]);
            setSelectedCard(null);
            setPaymentMethod("new");
          }
      };

      loadSavedCards();
    }

    // Add cart recalculation to ensure latest pricing with current referral
    const updateCart = async () => {
      try {
          const updatedCart = await recalculateCart(checkout);

        setCheckout((prev) => ({
          ...prev,
          cart: {
            ...prev.cart,
            shippingCost: updatedCart.shippingCost,
            shippingTotal: updatedCart.shippingTotal,
            shippingTotalString: updatedCart.shippingTotalString,
            orderTotal: updatedCart.orderTotal,
            orderTotalString: updatedCart.orderTotalString,
            subTotal: updatedCart.subTotal,
            totalDiscountRcString: updatedCart.totalDiscountRcString,
            totalDiscountRC: updatedCart.totalDiscountRC,
            taxTotal: updatedCart.taxTotal,
            taxTotalString: updatedCart.taxTotalString,
            discountOrder: updatedCart.discountOrder,
          },
        }));
      } catch (error) {
        console.error(error);
        sendErrorToSlack(error, {
          component: "Payment-CartRecalculation",
          userEmail: checkout.customer?.email,
          cartToken: checkout.cartToken,
        });
      }
    };

    updateCart();

    if (cart.cart.autoOrderItems && cart.cart.autoOrderItems.length > 0) {
      setCheckout(prev => ({ ...prev, SaveCreditCard: true }));
    }

    if (!isUserLoggedIn || paymentMethod === "new") {
      initializeTokenExClient();
    }
  }, [checkout.referredId, checkout.selectedReferral, checkout.referralChoice, isUserLoggedIn]);

  useEffect(() => {
    if (paymentMethod === "existing" && isUserLoggedIn) {
      setTokenExClient(null);
    } else if (paymentMethod === "new" && isUserLoggedIn) {
      initializeTokenExClient();
    }
  }, [paymentMethod, isUserLoggedIn, screenSize]);

  useEffect(() => {
    // Initialize acceptedTerms as false by default
    updateNestedContext("acceptedTerms", false, "billingAddress");
  }, []); // Empty dependency array means this runs once on mount

  const cancelOrder = () => {
    window.location = "https://424543.myshopify.com/collections/shop-all";
  };

  const reviewOrder = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    setValidated(true);
    
    let hasErrors = false;
    
    if (!sameAsShipping && validateBillingFields()) {
      hasErrors = true;
    }
    
    if ((paymentMethod === "new" || !isUserLoggedIn) && validateCreditCardFields()) {
      hasErrors = true;
    }
    
    if (!acceptedTerms) {
      setErrorMessage("You must accept the terms and conditions to continue.");
      hasErrors = true;
    }
    
    if (hasErrors) {
      event.stopPropagation();
      return;
    }
    if (isUserLoggedIn && paymentMethod === "existing") {
      if (form.checkValidity() === false) {
        event.stopPropagation();
        return;
      }
      setOrderStatus("valid");
      setCheckout({
        ...checkout,
        orderStatus: "valid"
      });
      return;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const cardYear = parseInt(checkout.creditcard.expirationYear);
    const cardMonth = parseInt(checkout.creditcard.expirationMonth);

    if (cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth)) {
      setMonthError("Card has expired");
      setYearError("Card has expired");
      event.stopPropagation();
      return;
    }

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    try {
      setValidToken(true);

      if (tokenExClient && form.checkValidity()) {
        tokenExClient.tokenize({
          success: function (data) {
            setIsCardValid(true);
            setCheckout({
              ...checkout,
              creditcard: {
                ...checkout.creditcard,
                last4: data.last4 || "",
              },
            });
            setOrderStatus("valid");
            setCheckout({
              ...checkout,
              orderStatus: "valid"
            });
            setToken(data);
          },
          error: function (data) {
            setIsCardValid(false);
            return;
          },
        });
      }
    } catch (error) {
      setIsCardValid(false);
      console.error("Unexpected error during tokenization:", error);
      sendErrorToSlack(error, {
        component: "Payment-CardTokenization",
        userEmail: checkout.customer?.email,
        cartToken: checkout.cartToken,
      });
    } finally {
      setValidToken(false);
    }
  };

  const confirmOrder = () => {
    setLoading(true);
    // Ensure shopifyCustomer exists
    if (!checkout.shopifyCustomer) {
      checkout.shopifyCustomer = {};
    }
    // Copy name data from shipping address if missing in shopifyCustomer
    if (!checkout.shopifyCustomer.firstName && checkout.shipping?.firstName) {
      checkout.shopifyCustomer.firstName = checkout.shipping.firstName;
    }
    if (!checkout.shopifyCustomer.lastName && checkout.shipping?.lastName) {
      checkout.shopifyCustomer.lastName = checkout.shipping.lastName;
    }

    // Determine the client type and if an upgrade should be performed
    const isEnrollingAsAmbassador = checkout.enrollType === "ambassador"; // Client is registering as ambassador
    const isUpgradingToAmbassador = checkout.upgradeType === "ambassador"; // Client is upgrading to ambassador
    const hasCustomerId = Boolean(checkout.customer?.id); // Client already has an ID

    // An ambassador upgrade should only occur when:
    // 1. An upgrade is specifically requested (upgradeType === "ambassador") or
    // 2. Registering as ambassador (enrollType === "ambassador") but already has a customer ID (means it's an existing customer changing to ambassador)
    const shouldUpgradeToAmbassador =
      isUpgradingToAmbassador || (isEnrollingAsAmbassador && hasCustomerId);

    let updatedCheckout = { ...checkout };

    if (cart.cart.autoOrderItems && cart.cart.autoOrderItems.length > 0) {
      updatedCheckout.SaveCreditCard = true;
    }

    if (isUserLoggedIn && paymentMethod === "existing" && selectedCard) {
      updatedCheckout = {
        ...updatedCheckout,
        creditcard: {
          ...updatedCheckout.creditcard,
          name: selectedCard.nameOnCard,
          expirationMonth: parseInt(selectedCard.expiryMonth, 10),
          expirationYear: parseInt(selectedCard.expiryYear, 10),
          savedCardToken: selectedCard.token || "",
          isSavedCard: true
        }
      };
    }

    const tokenizationData = isUserLoggedIn && paymentMethod === "existing" 
      ? { token: "", cardType: "" }
      : { token: token, cardType: updatedCheckout.creditcard.cardType || "" };

    setCheckout(updatedCheckout);

    submitOrder(updatedCheckout, tokenizationData)
      .then(async (response) => {
        if (response.status === 200) {
          const data = await response.json();
          if (data.success) {
            // Only execute ambassador upgrade if really needed
            if (shouldUpgradeToAmbassador && hasCustomerId) {
              try {
                // Get birth date from localStorage or use a default value
                const birthDate =
                  localStorage.getItem("ambassador_birthdate") || "";

                // Get the customer ID from the checkout context
                const shopCustomerId = checkout.customer?.id || "";
                const shopUrl = checkout.shopUrl || "";

                if (!shopCustomerId) {
                  sendErrorToSlack(
                    {
                      message: "Missing customer ID for Ambassador upgrade",
                      checkout: JSON.stringify(checkout),
                    },
                    {
                      component: "Payment-AmbassadorUpgrade",
                      userEmail: checkout.customer?.email,
                      cartToken: checkout.cartToken,
                    }
                  );
                }

                if (birthDate && shopCustomerId && shopUrl) {
                  const exigoCustomerId = checkout.customer?.exigoCustomerId;

                  await upgradeToAmbassador(exigoCustomerId, shopUrl, birthDate);
                  // Clear the localStorage values
                  localStorage.removeItem("ambassador_birthdate");
                  localStorage.removeItem("ambassador_upgrade");
                } else {
                  console.error(
                    "Missing required parameters for Ambassador upgrade",
                    {
                      birthDate,
                      shopCustomerId,
                      shopUrl,
                    }
                  );
                }
              } catch (upgradeError) {
                console.error("Error during Ambassador upgrade:", upgradeError);
                sendErrorToSlack(upgradeError, {
                  component: "Payment-AmbassadorUpgrade",
                  userEmail: checkout.customer?.email,
                  cartToken: checkout.cartToken,
                });
                // Continue with normal flow even if upgrade fails
              }
            }

            if (data.message) {
              sendErrorToSlack(data.message, {
                component: "Payment-OrderSubmission",
                userEmail: checkout.customer?.email,
                cartToken: checkout.cartToken,
              });
            }

            if (data.orderId) {
              setCheckout({
                ...checkout,
                orderId: data.orderId
              });
            }

            // Continue with normal flow
            if (checkout.cart.customerToken === "null") {
              nextStep();
            } else {
              window.location =
                "https://oolalife.com/account?orderpoints=" +
                checkout.orderPoints +
                "&clearcart=true";
            }
          } else {
            setErrorMessage(
              data.message || "Failed to submit order. Please try again."
            );
          }
        } else if (response.status === 400) {
          // Handle 400 status code
          setErrorMessage(
            "Failed to submit order. Please check your input and try again."
          );
        } else {
          // Handle other status codes
          setErrorMessage("Failed to submit order. Please try again.");
        }
      })
      .catch((error) => {
        setLoading(false);
        sendErrorToSlack(error, {
          component: "Payment-OrderSubmission",
          userEmail: checkout.customer?.email,
          cartToken: checkout.cartToken,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateNestedContext = (target, value, key) => {
    let newState = checkout[key];
    newState[target] = value;

    setCheckout({ ...checkout, [key]: newState });
  };

  const {
    creditcard: { name, expirationMonth, expirationYear, cvv },
    billingAddress: {
      sameAsShipping,
      acceptedTerms,
      country,
      state,
      streetAddress,
      city,
      zip,
    },
  } = checkout;

  const clearFieldError = (errorId) => {
    const errorSpan = document.getElementById(errorId);
    if (errorSpan) {
      errorSpan.textContent = '';
      errorSpan.style.display = 'none';
      errorSpan.classList.add('d-none');
    }
  };

  const validateBillingFields = () => {
    const fieldMappings = [
      { value: country, name: 'Country', errorId: 'error-country' },
      { value: streetAddress, name: 'Street Address', errorId: 'error-streetAddress' },
      { value: state, name: 'State', errorId: 'error-state' },
      { value: city, name: 'City', errorId: 'error-city' },
      { value: zip, name: 'ZIP Code', errorId: 'error-zip' }
    ];

    let hasErrors = false;

    fieldMappings.forEach(field => {
      const errorSpan = document.getElementById(field.errorId);
      if (errorSpan) {
        errorSpan.textContent = '';
        errorSpan.style.display = 'none';
        errorSpan.classList.add('d-none');
      }
    });

    fieldMappings.forEach(field => {
      if (!field.value || !field.value.trim()) {
        const errorSpan = document.getElementById(field.errorId);
        if (errorSpan) {
          errorSpan.textContent = `${field.name} is required`;
          errorSpan.style.display = 'contents';
          errorSpan.classList.remove('d-none');
        }
        hasErrors = true;
      }
    });

    return hasErrors;
  };

  const validateName = (value) => {
    setNameValidated(true);
    let errorMsg = "";
    if (!value?.trim()) {
      errorMsg = "Name is required";
    } else if (!/^[A-Za-z\s]+$/.test(value)) {
      errorMsg = "Name should only contain letters and spaces";
    }
    setNameError(errorMsg);
    return errorMsg;
  };

  const validateMonth = (value) => {
    setMonthValidated(true);
    let errorMsg = "";
    if (!value?.trim()) {
      errorMsg = "Month is required";
    } else {
      const month = parseInt(value);
      if (isNaN(month) || month < 1 || month > 12) {
        errorMsg = "Please enter a valid month (01-12)";
      } else {
        const formattedMonth = month.toString().padStart(2, "0");
        updateNestedContext("expirationMonth", formattedMonth, "creditcard");
      }
    }
    setMonthError(errorMsg);
    return errorMsg;
  };

  const validateYear = (value) => {
    setYearValidated(true);
    let errorMsg = "";
    if (!value?.trim()) {
      errorMsg = "Year is required";
    } else {
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 20;
      if (isNaN(year) || year < currentYear || year > maxYear || value.length !== 4) {
        errorMsg = `Enter a valid year (${currentYear}-${maxYear})`;
      }
    }
    setYearError(errorMsg);
    return errorMsg;
  };

  const validateCvv = (value) => {
    setCvvValidated(true);
    let errorMsg = "";
    if (!value?.trim()) {
      errorMsg = "CVV is required";
    } else if (!/^\d{3,4}$/.test(value)) {
      errorMsg = "Enter a valid CVV (3 or 4 digits)";
    }
    setCvvError(errorMsg);
    return errorMsg;
  };

  const validateCreditCardFields = () => {
    const nameErrorMsg = validateName(name);
    const monthErrorMsg = validateMonth(expirationMonth);
    const yearErrorMsg = validateYear(expirationYear);
    const cvvErrorMsg = validateCvv(cvv);
    
    return !!(nameErrorMsg || monthErrorMsg || yearErrorMsg || cvvErrorMsg);
  };

  return (
    <div>
      <div className="wizardContainer">
        <button className="wizardLink">Cart</button>
        {">"}
        <button className="wizardLink">Account Details</button>
        {">"}
        <button className="wizardLink" onClick={() => previousStep()}>
          Shipping Address
        </button>
        {">"}
        {!shouldSkipSubscription() && (
          <>
            <button className="wizardLink" onClick={() => previousStep()}>
              Subscription
            </button>
            {">"}
          </>
        )}
        <button className="wizardLink">Payment</button>
      </div>

      <SummaryTable
        contact={cart.customer.email}
        referral={checkout.referredId || cart.cart.referral}
        isPaymentPage={true}
      />

      {orderStatus === "invalid" && (
        <Form noValidate onSubmit={reviewOrder}>
          <Row className="mb-3">
            <ShipMethods shipMethods={cart.cart.shipMethods} />
          </Row>

          <Row className="mb-3 p-2">
            <Col md="12">
              <h2 className="marginBottomSmall marginTopSmall">Payment</h2>
              <p className="mb-0">All transactions are encrypted and secure.</p>
              <div className="paymentContainer p-0 ps-1">
                <div>
                  <Col xs="12" md="12" className="p-0">
                    {isUserLoggedIn && savedCards.length > 0 ? (
                      <div className="mb-3 ms-1">
                         <Form.Check
                           type="radio"
                           id="existing-card"
                           name="paymentMethod"
                           label={
                             <div className="d-flex align-items-center">
                               <div className="me-3">
                                 <i className="bi bi-credit-card-fill text-primary fs-4"></i>
                               </div>
                               <div className="d-flex">
                                 <p className="mb-0">Existing card ending in {selectedCard?.last4}</p>
                                 <p className="mb-0">, expiry {selectedCard?.expiryMonth}/{selectedCard?.expiryYear}</p>
                               </div>
                             </div>
                           }
                           className="px-3 d-flex"
                           checked={paymentMethod === "existing"}
                           onChange={() => setPaymentMethod("existing")}
                         />

                         <Form.Check
                           type="radio"
                           id="new-card"
                           name="paymentMethod"
                           label={
                             <div className="d-flex align-items-center">
                               <div className="me-3">
                                 <i className="bi bi-plus-circle text-success fs-4"></i>
                               </div>
                               <div>
                                 <p className="mb-0">Add a new card</p>
                               </div>
                             </div>
                           }
                           className="px-3 d-flex"
                           checked={paymentMethod === "new"}
                           onChange={() => setPaymentMethod("new")}
                         />
                      </div>
                    ) : null}



                    {(paymentMethod === "new" || !isUserLoggedIn) && (
                       <>
                        <Form.Group className="payment-name-group">
                          <Form.Label className="creditcardLabel">
                            NAME ON CARD
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={name}
                            className={`payment-form ${
                              nameError
                                ? "is-invalid"
                                : nameValidated && !nameError
                                  ? "is-valid"
                                  : ""
                            }`}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^[A-Za-z\s]*$/.test(value)) {
                                updateNestedContext("name", value, "creditcard");
                                setNameError("");
                              } else {
                                setNameError(
                                  "Name should only contain letters and spaces"
                                );
                              }
                            }}
                            onBlur={(e) => validateName(e.target.value)}
                            required
                          />
                          {nameError && (
                            <div
                              className="invalid-feedback"
                              style={{ display: "block" }}
                            >
                              {nameError}
                            </div>
                          )}
                        </Form.Group>
                        <Form.Group
                          className={`mb-3 cardnumber-group ${!isCardValid ? "was-validated" : ""}`}
                        >
                          <Form.Label className="creditcardLabel">
                            CARD NUMBER
                          </Form.Label>
                          <div id="tokenExIframeDiv"></div>

                          <div className="position-relative">
                            <input
                              type="hidden"
                              id="Token"
                              name="Token"
                              className={`creditcard-token form-control ${!isCardValid ? "is-invalid" : ""}`}
                            />
                            <input
                              type="hidden"
                              id="Display"
                              name="Display"
                              className="creditcard-display"
                            />
                            {!isCardValid && (
                              <div className="invalid-feedback">
                                <i className="bi bi-exclamation-circle-fill"></i>
                                Invalid Credit Card
                              </div>
                            )}
                          </div>
                        </Form.Group>

                        <Form.Group className="mb-3 expiration-group">
                          <Form.Label className="creditcardLabel">
                            EXPIRATION DATE
                          </Form.Label>
                          <div className="d-flex align-items-center position-relative expiration-date-container">
                            <div
                              className="position-relative"
                              style={{ width: "45%" }}
                            >
                              <Form.Control
                                type="text"
                                value={expirationMonth}
                                placeholder="MM"
                                maxLength={2}
                                className={`payment-form ${
                                  monthError
                                    ? "is-invalid"
                                    : monthValidated && !monthError
                                      ? "is-valid"
                                      : ""
                                }`}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, "");
                                  if (value.length <= 2) {
                                    updateNestedContext(
                                      "expirationMonth",
                                      value,
                                      "creditcard"
                                    );
                                    if (value) {
                                      const month = parseInt(value);
                                      if (month > 12) {
                                        setMonthError(
                                          "Please enter a valid month (01-12)"
                                        );
                                      } else {
                                        setMonthError("");
                                      }
                                    } else {
                                      setMonthError("");
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const errorMsg = validateMonth(e.target.value);
                                  e.target.setCustomValidity(errorMsg);
                                }}
                                required
                              />
                              {monthError && (
                                <div
                                  className="invalid-feedback"
                                  style={{ display: "block" }}
                                >
                                  {monthError}
                                </div>
                              )}
                            </div>
                            <span className="slash">/</span>
                            <div
                              className="position-relative"
                              style={{ width: "45%" }}
                            >
                              <Form.Control
                                type="text"
                                placeholder="YYYY"
                                maxLength={4}
                                value={expirationYear}
                                className={`payment-form ${
                                  yearError
                                    ? "is-invalid"
                                    : yearValidated && !yearError
                                      ? "is-valid"
                                      : ""
                                }`}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, "");
                                  if (value.length <= 4) {
                                    updateNestedContext(
                                      "expirationYear",
                                      value,
                                      "creditcard"
                                    );
                                    if (value.length === 4) {
                                      const year = parseInt(value);
                                      const currentYear = new Date().getFullYear();
                                      const maxYear = currentYear + 20;
                                      if (year < currentYear || year > maxYear) {
                                        setYearError(
                                          `Enter a valid year (${currentYear}-${maxYear})`
                                        );
                                      } else {
                                        setYearError("");
                                      }
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const errorMsg = validateYear(e.target.value);
                                  e.target.setCustomValidity(errorMsg);
                                }}
                                required
                              />
                              {yearError && (
                                <div
                                  className="invalid-feedback"
                                  style={{ display: "block" }}
                                >
                                  {yearError}
                                </div>
                              )}
                            </div>
                          </div>
                        </Form.Group>
                        <Form.Group className="mb-3 cvv-group">
                          <Form.Label className="creditcardLabel">CVV</Form.Label>
                          <Form.Control
                            type="text"
                            value={cvv}
                            maxLength={4}
                            className={`payment-form ${
                              cvvError
                                ? "is-invalid"
                                : cvvValidated && !cvvError
                                  ? "is-valid"
                                  : ""
                            }`}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              updateNestedContext("cvv", value, "creditcard");
                              if (value.length > 0 && !/^\d{3,4}$/.test(value)) {
                                setCvvError("Enter a valid CVV (3 or 4 digits)");
                              } else {
                                setCvvError("");
                              }
                            }}
                            onBlur={(e) => validateCvv(e.target.value)}
                            required
                          />
                          {cvvError && (
                            <div
                              className="invalid-feedback"
                              style={{ display: "block" }}
                            >
                              {cvvError}
                            </div>
                                                     )}
                         </Form.Group>
                         
                         {isUserLoggedIn && savedCards.length > 0 && paymentMethod === "new" && (
                           <div className="mb-1">
                             <Form.Check
                               type="checkbox"
                               id="replace-primary-card"
                               label="Replace primary card"
                               checked={checkout.SaveCreditCard || false}
                               className="fw-normal mb-0"
                               onChange={(e) => {
                                 setCheckout({ ...checkout, SaveCreditCard: e.target.checked });
                               }}
                             />
                           </div>
                         )}
                         {((savedCards.length === 0) || (cart.cart.autoOrderItems && cart.cart.autoOrderItems.length > 0)) && (
                           <div className="mb-1">
                             <Form.Check
                               type="checkbox"
                               id="save-card"
                               label={!isUserLoggedIn && cart.cart.autoOrderItems.length > 0 
                                 ? "Save card for auto-order payments" 
                                 : "Save card for future purchases"}
                               checked={!isUserLoggedIn && cart.cart.autoOrderItems.length > 0 
                                 ? true 
                                 : (checkout.SaveCreditCard)}
                               className="fw-normal mb-0"
                               onChange={(e) => {
                                 setCheckout({ ...checkout, SaveCreditCard: e.target.checked });
                               }}
                               disabled={!isUserLoggedIn && cart.cart.autoOrderItems.length > 0}
                             />
                           </div>
                         )}
                       </>
                     )}
                  </Col>
                </div>
              </div>

              <div className="billingContainer ps-1">
                <div>
                  <Col xs="12" md="12" className="p-0">
                    <Form.Check
                      type="checkbox"
                      label="Billing address same as shipping"
                      className="mb-3"
                      checked={sameAsShipping}
                      onChange={(e) => {
                        updateNestedContext(
                          "sameAsShipping",
                          e.target.checked,
                          "billingAddress"
                        );
                      }}
                    />
                    {!sameAsShipping && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label className="creditcardLabel">
                            COUNTRY
                          </Form.Label>
                          <Select
                            name="country"
                            value={country}
                            collection="country"
                            nestedName="billingAddress"
                            floatingLabel={true}
                            floatingLabelText="Country"
                            updateContext={(target, value, key) => {
                              updateNestedContext(target, value, key);
                              if (value.trim()) {
                                clearFieldError("error-country");
                              }
                            }}
                          />
                          <span 
                            id="error-country" 
                            className="invalid-feedback d-none error-select-country" 
                            style={{ display: "none" }}
                          >
                          </span>
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="creditcardLabel">
                            STREET ADDRESS
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={streetAddress}
                            className="payment-form p-2 ps-3 input-street"
                            onChange={(e) => {
                               updateNestedContext(
                                "streetAddress",
                                e.target.value,
                                "billingAddress"
                               );
                               if (e.target.value.trim()) {
                                 clearFieldError('error-streetAddress');
                               }
                             }}
                           />
                           <span 
                             id="error-streetAddress" 
                             className="invalid-feedback d-none error-input-street" 
                             style={{ display: "none" }}
                           >
                           </span>
                        </Form.Group>
                         <Form.Group className="mb-3">
                           <Form.Label className="creditcardLabel">
                             STATE
                           </Form.Label>
                           <Select
                             name="state"
                             value={state}
                             className="payment-form-select p-2 select-state"
                             collection={
                               country === "CA"
                                 ? "caProvince"
                                 : country === "AU"
                                   ? "auState"
                                   : "usState"
                             }
                             nestedName="billingAddress"
                             floatingLabel={true}
                             floatingLabelText="State"
                             updateContext={(target, value, key) => {
                               updateNestedContext(target, value, key);
                               if (value.trim()) {
                                 clearFieldError("error-state");
                               }
                             }}
                           />
                           <span 
                             id="error-state" 
                             className="invalid-feedback d-none error-select-state" 
                             style={{ display: "none" }}
                           >
                           </span>
                         </Form.Group>

                        <Row>
                          <Col xs="12" md="6">
                            <Form.Group className="mb-3">
                              <Form.Label className="creditcardLabel">
                                CITY
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={city}
                                className="payment-form p-2 ps-3 input-city"
                                 onChange={(e) => {
                                   updateNestedContext(
                                    "city",
                                    e.target.value,
                                    "billingAddress"
                                   );
                                   if (e.target.value.trim()) {
                                     clearFieldError('error-city');
                                   }
                                 }}
                               />
                               <span 
                                 id="error-city" 
                                 className="invalid-feedback d-none error-input-city" 
                                 style={{ display: "none" }}
                               >
                               </span>
                            </Form.Group>
                          </Col>
                          <Col xs="12" md="6">
                            <Form.Group className="mb-3">
                              <Form.Label className="creditcardLabel">
                                ZIPCODE
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={zip}
                                className="payment-form p-2 ps-3 input-zip"
                                 onChange={(e) => {
                                   updateNestedContext(
                                    "zip",
                                    e.target.value,
                                    "billingAddress"
                                   );
                                   if (e.target.value.trim()) {
                                     clearFieldError('error-zip');
                                   }
                                 }}
                               />
                               <span 
                                 id="error-zip" 
                                 className="invalid-feedback d-none error-input-zip" 
                                 style={{ display: "none" }}
                               >
                               </span>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Col>
                </div>
              </div>

              <div className="termsContainer ps-1">
                <Form.Check
                  type="checkbox"
                  label={
                    <span>
                      I agree to the{" "}
                      <a
                        href="https://myoola.oolalife.com/Content/docs/CustomerAgreement.pdf"
                        className="oola-main-text-color px-1"
                      >
                        customer terms and conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://myoola.oolalife.com/Content/docs/RefundPolicy.pdf"
                        className="oola-main-text-color px-1"
                      >
                        refund policy
                      </a>
                    </span>
                  }
                  className="mb-3"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    updateNestedContext(
                      "acceptedTerms",
                      e.target.checked,
                      "billingAddress"
                    );
                    if (e.target.checked && errorMessage === "You must accept the terms and conditions to continue.") {
                      setErrorMessage("");
                    }
                  }}
                  required
                />
              </div>
            </Col>
          </Row>
        </Form>
      )}

      <div className="flex JCEnd">
        {orderStatus === "valid" ? (
          <>
            <Button
              className="btn-bold secondary-color w-50 m-2"
              variant="danger"
              onClick={cancelOrder}
            >
              Cancel Order
            </Button>
            <Button
              className="btn-bold oola-main-color w-50 m-2"
              variant="success"
              disabled={isLoading}
              onClick={!isLoading ? confirmOrder : null}
            >
              {isLoading ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading..</span>
                </Spinner>
              ) : (
                "Confirm Order"
              )}
            </Button>
          </>
        ) : (
          <Button
            className="btn-bold oola-main-color btn-primary w-50"
            variant="primary"
            onClick={reviewOrder}
            disabled={isValidToken}
          >
            {isValidToken ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mx-3"
                />
                Validating Credit Card...
              </>
            ) : (
              "Review Order"
            )}
          </Button>
        )}
      </div>
      {errorMessage && (
        <p className="mt-4 text-danger text-center">{errorMessage}</p>
      )}
    </div>
  );
};

export default Payment;